import React, { useEffect, useRef, useState } from 'react'
import BoxHeaderNew, { DEFAULT_SLIPPAGE } from './BoxHeader/BoxHeader'
import PrimaryButton from './PrimaryButton'
import { useStoreActions, useStoreState } from '../model/hooks'
import { HelptipIcon } from '../svgIcons/HelptipIcon'
import { CircleIcon } from '../svgIcons/CircleIcon'
import { CLPosition } from '../model/clpool'
import { calculateRatioFromTick, calculateRatioFromTickForTokens, formatFloatingPointNumber, formatNumber, numFormat } from '../blockchainUtils/math'
import { AlertIcon } from '../svgIcons/AlertIcon'
import Spinner from './Spinner'
import { SwitchIcon } from '../svgIcons/SwitchIcon'
import { migrateLegacyDestinationRange, migrateLegacyPositionDestination_phase0_fees_and_range, migrateLegacyPosition_phase0 } from '../services/position'
import { Network } from '../services/token.service'
import { useWalletContext } from './WalletContext'
import Popup from './Popup'
import { ArrowRightIcon } from '../svgIcons/ArrowRightIcon'
import { PlusIcon } from '../svgIcons/PlusIcon'
import BigNumber from 'bignumber.js'
import { fetchPoolPrice, getAllCLPoolForFee, getCLPool } from '../model/clpool/helper'
import { LegacyMigrationQuote, simulateMigrateLegacyPosition_phase0 } from '../services/migrate_legacy'
import { Token } from '../atoms'
import RebalancePopup from './RebalancePopup/RebalancePopup'
import { XIcon } from '../svgIcons/XIcon'
import YouTube from 'react-youtube'
import { YoutubePlayIcon } from '../svgIcons/YoutubePlayIcon'
import { calculateTotalLiquidityUsdPerPosition, calculateTotalUnclaimedFees, getPercentageOfLiquidityForPosition } from '../blockchainUtils/positionAmountsMath'
import { RewardBadgeIcon } from '../svgIcons/RewardBadgeIcon'
import { tokenAddresses } from './IncentivePage/IncentivePage'
import { useNavigate } from 'react-router'
import { PoolOverview, loadPoolOverviewData } from './charts/chartsApi'
import { MAX_TICK } from './PoolsPage/helper'

export const possiblePools = [
    [tokenAddresses.ETH, tokenAddresses.USDC],
    [tokenAddresses.USDC, tokenAddresses.USDT],
    [tokenAddresses.STRK, tokenAddresses.ETH],
    [tokenAddresses.STRK, tokenAddresses.USDC],
    [tokenAddresses.WBTC, tokenAddresses.ETH],
    [tokenAddresses.WSTETH, tokenAddresses.ETH],
    [tokenAddresses.USDC, tokenAddresses.DAI]
]

export function PositionsV3(props: {
    isLegacy?: boolean
    network: string
    onAddPosition: () => void
    onSelectPosition: (index: number) => void
    isLoading: boolean
    isScrollToView: boolean
}) {
    const { onAddPosition, network, onSelectPosition, isLoading = false, isLegacy = false, isScrollToView = false } = props
    const { walletAddress: connectedAddress } = useWalletContext()

    const postions = useStoreState((state) => state.clPools.getCLPositions(network || ''))
    const legacyPositons = useStoreState((state) => state.clPools.getCLLegacyPositions(network || ''))
    const positionsToUse = isLegacy ? legacyPositons : postions
    const getToken = useStoreState((state) => state.tokens.getToken)
    const [tokenPerReversed, setTokenPerReversed] = useState({} as Record<number, boolean>)
    const [isClaiming, setIsClaiming] = useState({} as Record<string, boolean>)
    const loadTransaction = useStoreActions((actions) => actions.transaction.loadTransaction)
    const [withdrawTxHash, setWithdrawTxHash] = useState('')
    const transaction = useStoreState((state) => state.transaction.getTransaction(withdrawTxHash))
    const loadPositons = useStoreActions((actions) => actions.clPools.loadPositons)
    const loadLegacyPositons = useStoreActions((actions) => actions.clPools.loadLegacyPositons)
    const loadBalances = useStoreActions((actions) => actions.tokens.loadBalances)
    // migrate phase0 popup stuff
    const [migratePopupOpen, setMigratePopupOpen] = useState(false)
    const [legacyPositionToMigrate, setLegacyPositionToMigrate] = useState<CLPosition>()
    const [isMigratePopupLoading, setIsMigratePopupLoading] = useState(false)
    const [legacyMigrationQuote, setLegacyMigrationQuote] = useState<LegacyMigrationQuote>()
    const [selectedRebalance, setselectedRebalance] = useState('')
    const [isTutorialShow, setIsTutorialShow] = useState(false)
    const [isTutorialVideoVisible, setIsTutotrialVideoVisible] = useState(false)
    const navigate = useNavigate()
    const myRef = useRef(null)
    const [aprOverview, setAprOverview] = useState({} as Record<string, { apr: number; incentiveApr: number }>)

    useEffect(() => {
        if (window.localStorage.getItem('didViewVideo') !== 'true') setIsTutorialShow(true)
        loadOverview()
    }, [])
    useEffect(() => {
        if (isScrollToView && myRef && myRef.current && myRef.current) (myRef.current as any).scrollIntoView({ block: 'end', behavior: 'smooth' })
    }, [legacyPositons])

    useEffect(() => {
        if (transaction && (transaction.status === 'ACCEPTED_ON_L2' || transaction.status === 'ACCEPTED_ON_L1')) {
            loadLegacyPositons({ network: network as Network })
            loadPositons({ network: network as Network, address: connectedAddress! })
            loadBalances({ network: network as Network, walletAddress: connectedAddress! })
            setIsClaiming({})
            setWithdrawTxHash('')
        }
    }, [transaction])

    async function loadOverview() {
        const ids = [
            '0x503f36ee47238549e73e691a840dca44530ed8a2dc98c9bd5adaaacec759536',
            '0x43b4920a31bfdbaa560a78b62c82435934af2b026305d11d0624d9fba40b5',
            '0x71273c5c5780b4be42d9e6567b1b1a6934f43ab8abaf975c0c3da219fc4d040',
            '0x7c5b7061e9e2e233b6ba9e084c3574c6575495721b18344c251d0234e1a77a3'
        ]
        const calls = [] as Promise<PoolOverview | undefined>[]
        ids.map((id) => {
            calls.push(loadPoolOverviewData(id))
        })
        const response = await Promise.all(calls)
        const overView = {} as Record<string, { apr: number; incentiveApr: number }>
        response.map((item, index) => {
            if (!item) return
            overView[ids[index]] = { apr: item?.apr.apr_cl, incentiveApr: item?.apr.incentive_apr }
        })
        setAprOverview(overView)
    }

    async function onClaimLegacy_phase0(position: CLPosition, fee: number, rangePercent: number) {
        setIsClaiming({ [position.legacyPoolId!]: true })
        try {
            const res = await migrateLegacyPosition_phase0(network as Network, position, fee, rangePercent)

            if (res && res.success) {
                // loadTransaction({ [res.tx!.hash]: res.tx! })
                const timestampSent = new Date().getTime()

                loadTransaction({ txPayload: { [res.tx!.hash]: { ...res.tx!, labelFields: { timestampSent: timestampSent } } }, isGoogleLog: network === 'mainnet-alpha' })
                setWithdrawTxHash(res.tx!.hash)
            } else {
                setIsClaiming({})
                console.log('Failed to migrate legacy')
            }
        } catch (err: any) {
            setIsClaiming({})
            console.log('err ', err.message)
        }
    }

    const showMigrateLegacyPopup = (position: CLPosition) => {
        setLegacyPositionToMigrate(position)
        setIsMigratePopupLoading(true)

        setMigratePopupOpen(true)

        // console.log('YSYS migrating position=', position)
        calculateMigrationData(position)
    }

    const calculateMigrationData = async (position: CLPosition) => {
        try {
            const token0 = getToken(network, position.token0)
            const token1 = getToken(network, position.token1)
            const fee = migrateLegacyPositionDestination_phase0_fees_and_range(network as Network, position)

            if (!token0 || !token1 || !fee) return
            // read the destination pool
            const destPool = await getAllCLPoolForFee(network as Network, token0, token1, fee.new * 10000)
            await fetchPoolPrice(network as Network, destPool)
            // console.log('YSYS destPool=', destPool)
            const res = await simulateMigrateLegacyPosition_phase0(network as Network, position, fee.new, fee.range)

            if (res === undefined) throw 'Cannot simulate the legacy migration tx'
            setLegacyMigrationQuote(res)
            setIsMigratePopupLoading(false)
        } catch (e) {
            console.error('ERROR ', e)
            setIsMigratePopupLoading(false)
            setMigratePopupOpen(false)
        }
    }

    function renderLegacyPositionFromTo(token0: Token, token1: Token, amount0: string, amount1: string, fee: number) {
        if (amount0.length > 10 || amount1.length > 10) {
            return (
                <div className='flex flex-col justify-center items-center w-full'>
                    <div className='flex items-center'>
                        <img className='shrink-0 w-[20px] h-[20px]' style={{ marginRight: 8 }} src={token0!.icon} alt='' />
                        <span>{formatFloatingPointNumber(amount0)}</span>
                        <span className='ml-2'>{token0?.symbol}</span>
                        <span className='mx-2'>/</span>
                    </div>
                    <div className='flex items-center'>
                        <img className='shrink-0 w-[20px] h-[20px]' style={{ marginRight: 8 }} src={token1!.icon} alt='' />
                        <span>{formatFloatingPointNumber(amount1)}</span>
                        <span className='ml-2 mr-2'>{token1?.symbol}</span>
                        <span className='mr-2 text-[#0AE8F9]'>{fee + '%'}</span>
                    </div>
                </div>
            )
        }

        return (
            <div className='flex items-center w-full justify-center'>
                <div className='flex items-center justify-center w-full'>
                    <img className='shrink-0 w-[20px] h-[20px]' style={{ marginRight: 8 }} src={token0!.icon} alt='' />
                    <span>{formatFloatingPointNumber(amount0)}</span>
                    <span className='ml-2'>{token0?.symbol}</span>
                    <span className='mx-2'>/</span>
                </div>
                <div className='flex items-center justify-center w-full'>
                    <img className='shrink-0 w-[20px] h-[20px]' style={{ marginRight: 8 }} src={token1!.icon} alt='' />
                    <span>{formatFloatingPointNumber(amount1)}</span>
                    <span className='ml-2 mr-2'>{token1?.symbol}</span>
                    <span className='mr-2 text-[#0AE8F9]'>{fee + '%'}</span>
                </div>
            </div>
        )
    }

    function renderMigrateLegacyPopup_phase0() {
        const position = legacyPositionToMigrate
        if (position === undefined) {
            return <></>
        }
        // const poolDestination = migrateLegacyPositionDestination_phase0_tokens(network as Network, position)
        const token0 = getToken(network, position.token0)
        const token1 = getToken(network, position.token1)
        const fee = migrateLegacyPositionDestination_phase0_fees_and_range(network as Network, position)

        if (!token0 || !token1 || !fee) {
            return <></>
        }
        if (isMigratePopupLoading) {
            // destination pool state is still loading
            // show a spinner in the meantime
            return (
                //@ts-ignore
                <Popup background='#151D44' title='Migrate Position' onClose={() => setMigratePopupOpen(false)} open={migratePopupOpen}>
                    <div className='mt-4 w-full text-center text-[18px]'>
                        {/* <Typography> */}
                        <span className='block  font-bold'>
                            Migrate your old mySwap position to mySwap concentrated liquidity position and enjoy much greater capital efficiency.
                        </span>
                        <span className='block mt-4  font-bold'> You will be minted an NFT for your migrated position.</span>
                        <span className='w-full text-center flex justify-center font-bold mt-[32px] text-[18px]'>Calculating Migration Flow...</span>
                    </div>
                </Popup>
            )
        }

        // read the destination pool and migration flow information
        // as it was already loaded
        const destPool = getCLPool(network as Network, token0, token1, fee.new * 10000)

        if (destPool === undefined) {
            // console.log('YSYS No dest pool')
            return <></>
        }
        // console.log('YSYS destPool=', destPool)

        const { tick_lower, tick_upper } = migrateLegacyDestinationRange(network as Network, destPool, fee.range)

        let ratio_low = calculateRatioFromTickForTokens(tick_lower, token0, token1)
        let ratio_hi = calculateRatioFromTickForTokens(tick_upper, token0, token1)
        let flip = false
        // flip if needed
        if (ratio_hi.isLessThan(1)) {
            flip = true
            const tmp = ratio_low
            ratio_low = BigNumber(1).dividedBy(ratio_hi)
            ratio_hi = BigNumber(1).dividedBy(tmp)
        }

        const v1Amount0 = legacyMigrationQuote && legacyMigrationQuote.v1Amount0 ? legacyMigrationQuote.v1Amount0.toString() : '0'
        const v1Amount1 = legacyMigrationQuote && legacyMigrationQuote.v1Amount1 ? legacyMigrationQuote.v1Amount1.toString() : '0'
        const clAmount0 = legacyMigrationQuote && legacyMigrationQuote.clAmount0 ? legacyMigrationQuote.clAmount0.toString() : '0'
        const clAmount1 = legacyMigrationQuote && legacyMigrationQuote.clAmount1 ? legacyMigrationQuote.clAmount1.toString() : '0'
        return (
            //@ts-ignore
            <Popup background='#151D44' title='Migrate Position' onClose={() => setMigratePopupOpen(false)} open={migratePopupOpen}>
                <div className='mt-4 w-full text-center text-[18px]'>
                    <div className='flex flex-col mt-[24px] items-center'>
                        <div className='border border-[1px] border-[#0C3F6D] rounded-[8px] w-full px-6 py-4'>
                            {renderLegacyPositionFromTo(token0!, token1, v1Amount0, v1Amount1, fee.original)}
                        </div>
                        <div className='rounded-full flex items-center justify-center w-[36px] h-[36px] border-[2px] border-[#0C3F6D] my-2'>
                            <ArrowRightIcon className='w-[20px] h-[20px] my-2' style={{ transform: 'rotate(90deg)' }} />
                        </div>
                        <div className='flex flex-col items-center border border-[1px] border-[#0C3F6D] rounded-[8px] w-full px-6 py-4'>
                            {renderLegacyPositionFromTo(token0, token1, clAmount0, clAmount1, fee.new)}
                            <div className='h-[1px] w-full bg-[#0C3F6D] mt-[10px]' />
                            <div className='flex flex-col w-full items-center'>
                                <div className='flex items-center mt-4 text-white justify-center w-full'>
                                    <div className='relative ml-2 group flex'>
                                        <HelptipIcon className='w-[16px] h-[16px] text-[white] mr-1' />
                                        <span className='w-[268px] left-[-133px] group-hover:visible invisible group-hover:opacity-100 transition-opacity bg-[#ffffffd9] text-[16px] text-black rounded-md absolute opacity-0 top-[-78px] p-3 z-[1]'>
                                            Range of applicable price intervals for asset exchange
                                        </span>
                                    </div>
                                    <span className='text-[16px]'>Range:</span>
                                </div>
                                <div className='flex items-center'>
                                    <span className='font-bold mr-1'>{formatFloatingPointNumber(ratio_low.toString())}</span>
                                    <span>to </span>
                                    <span className='font-bold mx-1'>
                                        {formatFloatingPointNumber(ratio_hi.toString())}
                                        {flip ? token0.symbol : token1.symbol}{' '}
                                    </span>
                                    <span>{' per '}</span>
                                    <span className='ml-1 font-bold'>{flip ? token1.symbol : token0.symbol}</span>
                                </div>
                            </div>
                        </div>

                        <div className='flex items-center mt-6 text-[16px]'>
                            <div className='relative ml-2 group flex'>
                                <HelptipIcon className='mr-2 w-[16px] h-[16px] text-[white] mr-1' />
                                <span className='w-[268px] left-[-133px] group-hover:visible invisible group-hover:opacity-100 transition-opacity bg-[#ffffffd9] text-[16px] text-black rounded-md absolute opacity-0 top-[-78px] p-3 z-[1]'>
                                    Remaining balance returns to your wallet after the migration
                                </span>
                            </div>
                            <span>Leftovers go back to you wallet:</span>
                        </div>
                        <span className='font-bold text-[16px]'>
                            {formatFloatingPointNumber(legacyMigrationQuote?.leftoverAmount0!)} {token0.symbol} ,{' '}
                            {formatFloatingPointNumber(legacyMigrationQuote?.leftoverAmount1!)} {token1.symbol}
                        </span>
                    </div>
                </div>
                <PrimaryButton
                    size='lg'
                    className='mt-[40px] rounded-[16px] py-1 px-1 lg:py-1 lg:px-1'
                    onClick={() => {
                        onClaimLegacy_phase0(position, fee.new, fee.range)
                        setMigratePopupOpen(false)
                    }}>
                    <span className='text-[16px]'>Migrate</span>
                </PrimaryButton>
            </Popup>
        )
    }

    function renderRebalancePopup() {
        if (!selectedRebalance) return null

        return <RebalancePopup isOpen={Boolean(selectedRebalance)} onClose={() => setselectedRebalance('')} positionIndex={Number(selectedRebalance)} />
    }

    // eslint-disable-next-line no-unused-vars
    function renderInOutRange(position: CLPosition, index: number) {
        if (isLegacy) return null

        const isOutOfRange = position.poolCurrentTick > position.tickUpper || position.poolCurrentTick < position.tickLower
        return (
            <div className={`flex items-center text-[#${isOutOfRange ? 'FFBC6C' : '68F2B0'}] text-[12px] flex-shrink-0 ml-2`}>
                {isOutOfRange ? <AlertIcon className='w-[16px] h-[16px]' /> : <HelptipIcon className='w-[16px] h-[16px]' />}
                <span className='ml-1'>{isOutOfRange ? 'Out Of Range' : 'In Range'}</span>
                <CircleIcon className='w-[6px] h-[6px] ml-1' />
                {isOutOfRange ? renderRebalanceButton(position, index) : null}
            </div>
        )
    }

    function renderRebalanceButton(position: CLPosition, index: number, isMobile = false) {
        const isOutOfRange = position.poolCurrentTick > position.tickUpper || position.poolCurrentTick < position.tickLower
        if (!isOutOfRange) return null

        return (
            <PrimaryButton
                onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setselectedRebalance(position.positionIndex.toString())
                }}
                className={`${isMobile ? 'mt-4 lg:hidden' : 'hidden lg:flex ml-3'}`}
                size='sm'>
                <span className='text-[18px]'>Rebalance</span>
            </PrimaryButton>
        )
    }

    function renderTokenPerToken(position: CLPosition, index: number) {
        if (isLegacy) {
            return null
        }
        const token1 = getToken(network, position.token0)
        const token2 = getToken(network, position.token1)
        let bottomRate = token1 !== null && token2 !== null ? numFormat(calculateRatioFromTickForTokens(position.tickLower - MAX_TICK, token1, token2).toString()) : 0
        let topRate = token1 !== null && token2 !== null ? numFormat(calculateRatioFromTickForTokens(position.tickUpper - MAX_TICK, token1, token2).toString()) : 0
        if (tokenPerReversed[index]) {
            const oldBottomRate = bottomRate
            const oldTopRate = topRate
            bottomRate = numFormat(BigNumber(1).dividedBy(BigNumber(oldTopRate)).toString())
            topRate = numFormat(BigNumber(1).dividedBy(BigNumber(oldBottomRate)).toString())
        }
        const firstTokenSymbol = tokenPerReversed[index] ? token1?.symbol : token2?.symbol
        const secondTokenSymbol = tokenPerReversed[index] ? token2!.symbol : token1?.symbol
        const isTopRateInfinity = BigNumber(topRate).gt(10 ** 17)
        const isBottomRateInfinity = BigNumber(bottomRate).gt(10 ** 17)
        return (
            <div
                className='flex flex-col mt-4 w-fit'
                onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setTokenPerReversed({ ...tokenPerReversed, [index]: !tokenPerReversed[index] })
                }}>
                <div>
                    <span>{`${firstTokenSymbol} -> ${secondTokenSymbol}`}</span>
                </div>
                <div className='cursor-pointer flex text-[16px] items-center text-white mt-2'>
                    <span className='text-[#0A7C9B] mr-1'>Min</span>
                    <span className='mr-1'>{`${isBottomRateInfinity ? 'Infinity' : formatFloatingPointNumber(bottomRate.toString())}`}</span>
                    <span className='text-[#0A7C9B] mr-1'>/ Max</span>
                    <span className='mr-1'>{`${isTopRateInfinity ? 'Infinity' : formatFloatingPointNumber(topRate.toString())}`}</span>
                    <SwitchIcon className='ml-4 w-[18px] h-[18px]' />
                </div>
            </div>
        )
    }

    function renderLegacyActions_phase0(position: CLPosition) {
        if (!isLegacy) return null

        return (
            <div className='flex w-full justify-between mt-4'>
                <PrimaryButton
                    isLoading={position.legacyPoolId ? isClaiming[position.legacyPoolId!] : false}
                    isDisabled={position.legacyPoolId ? isClaiming[position.legacyPoolId!] : false}
                    size='lg'
                    className='w-full'
                    onClick={() => showMigrateLegacyPopup(position)}>
                    <span className='text-[16px]'>Migrate</span>
                </PrimaryButton>
            </div>
        )
    }

    function renderTotalPositionValue(position: CLPosition) {
        if (isLegacy) return null

        const totalUsdValue = calculateTotalLiquidityUsdPerPosition(position!, network as Network)
        return (
            <div>
                <div className='w-full h-[1px] bg-[#0C3F6D] mb-4 mt-4' />
                {renderUnclaimedFees(position)}
                <div className='flex items-center justify-between'>
                    <span className='text-[16px] text-white font-bold'>Total Position Value </span>
                    <span>{`$${formatNumber(Number(totalUsdValue))}`}</span>
                </div>
            </div>
        )
    }

    function renderUnclaimedFees(position: CLPosition) {
        if (isLegacy) return null

        const totalUsdValue = calculateTotalUnclaimedFees(position!, network as Network)
        return (
            <div>
                {/* <div className='w-full h-[1px] bg-[#0C3F6D] mb-4 mt-4' /> */}
                <div className='flex items-center justify-between'>
                    <span className='text-[16px] text-white font-bold'>Unclaimed fees </span>
                    <span>{`$${formatNumber(Number(totalUsdValue))}`}</span>
                </div>
            </div>
        )
    }

    function renderIncentiveBadge(token1: string, token2: string) {
        const isMatch = possiblePools.some((pool) => [token1, token2].every((token) => pool.includes(token)))

        if (!isMatch) return null
        return (
            <div className='relative flex items-center'>
                <div className='ml-2 group flex'>
                    <RewardBadgeIcon className='ml-2 w-[30px] h-[30px]' />
                    <span
                        style={{ background: 'rgba(255, 255, 255, 0.85' }}
                        className='w-[180px] left-[17px] group-hover:visible invisible group-hover:opacity-100 transition-opacity text-sm text-black rounded-md absolute opacity-0 top-[-36px] p-3 z-[1]'>
                        Eligible for incentive
                    </span>
                </div>
            </div>
        )
    }

    function renderIncentiveAPR(poolKey: string) {
        const totalApr = aprOverview[poolKey] ? aprOverview[poolKey].apr + aprOverview[poolKey].incentiveApr : 0
        if (!totalApr) return null
        const incentiveApr = aprOverview[poolKey] ? aprOverview[poolKey].incentiveApr : 0
        return (
            <div style={{ color: 'rgba(255, 255, 255, 0.50)' }} className='mt-[12px] flex items-center'>
                <div className='font-bold bg-[#0C3F6D] flex items-center h-[24px] rounded-[8px] p-2 '>
                    <span>Total APR:</span>
                    <span className='ml-1 text-[#0AE8F9]'>{`${numFormat(totalApr.toFixed(2))}%`}</span>
                </div>
                <span className='text-[12px]  ml-[10px]'>{`(${numFormat(incentiveApr.toFixed(2))}% Incentive)`}</span>
            </div>
        )
    }

    function renderPool(position: CLPosition, index: number) {
        if (!position || !position.token0 || !position.token1) return null
        const token1 = getToken(network, position.token0)
        const token2 = getToken(network, position.token1)

        const { percentage1, percentage2 } = getPercentageOfLiquidityForPosition(calculateRatioFromTick(position.poolCurrentTick - MAX_TICK).toString(), position)

        return (
            <div
                key={index}
                onClick={() => (isLegacy ? null : onSelectPosition(position.positionIndex))}
                className='cursor-pointer flex flex-col lg:hover:bg-[#151D44] bg-[#1D285A] p-4 rounded-[8px] mt-6'
                style={{ boxShadow: '0px 3px 7px rgba(28, 28, 34, 0.33)' }}>
                <div className='flex items-center justify-between cursor-pointer'>
                    <div className='flex items-center'>
                        <div className='flex items-center'>
                            <div>
                                <img src={token1!.icon} alt='' width={24} height={24} />
                            </div>
                            <div className='relative left-[-10px]'>
                                <img src={token2!.icon} alt='' width={24} height={24} />
                            </div>
                            <span className='text-[18px] text-white font-bold'>{`${token1!.symbol} / ${token2?.symbol}`}</span>
                        </div>
                        {isLegacy ? null : <div className='ml-2 bg-[#0C3F6D] p-1 rounded-[8px] text-[#0AE8F9] text-[16px] text-center'>{`${position.fee / 10000}%`}</div>}
                        {renderIncentiveBadge(position.token0, position.token1)}
                    </div>
                    {renderInOutRange(position, index)}
                </div>
                {renderIncentiveAPR(position.poolAddress)}
                {renderTokenPerToken(position, index)}
                <div className='w-full h-[1px] bg-[#0C3F6D] mb-4 mt-4' />
                <div className='flex flex-col' onClick={() => (isLegacy ? null : onSelectPosition(index))}>
                    <div className='flex justify-between items-center'>
                        <span className='font-bold'>{token1!.symbol}</span>
                        <div className='flex items-center'>
                            <span>{position && position.value && position.value.token1Amount ? formatFloatingPointNumber(position.value.token1Amount) : 0}</span>
                            {isLegacy ? null : (
                                <span className='ml-1 text-[#0AE8F9] text-[16px] bg-[#0C3F6D] px-1 rounded-[8px] flex items-center justify-center'>{`${percentage1}%`}</span>
                            )}
                        </div>
                    </div>
                    <div className='mt-2 flex justify-between items-center'>
                        <span className='font-bold'>{token2!.symbol}</span>
                        <div className='flex items-center'>
                            <span>{position && position.value && position.value.token2Amount ? formatFloatingPointNumber(position.value.token2Amount) : 0}</span>
                            {isLegacy ? null : (
                                <span className='ml-1 text-[#0AE8F9] text-[16px] bg-[#0C3F6D] px-1 rounded-[8px] flex items-center justify-center'>{`${percentage2}%`}</span>
                            )}
                        </div>
                    </div>
                </div>
                {/* {renderUnclaimedFees(position)} */}
                {renderTotalPositionValue(position)}
                {renderLegacyActions_phase0(position)}
                {/* {renderRebalanceButton(position, index, true)} */}
            </div>
        )
    }

    function renderLoadingScreen() {
        if (isLegacy) return null
        if (!connectedAddress) return <span className='text-[32px] font-bold mt-4 block'>Connect to your wallet</span>
        return (
            <div className='flex flex-col justify-center items-center  text-[24px]'>
                {renderNoPositions()}
                <span className='mt-4'>{`Loading ${isLegacy ? 'legacy positions' : 'positions'}`}</span>
                <Spinner height='40px' width='40px' className='mt-4' />
            </div>
        )
    }

    function renderNoPositions(isEmptyState = true) {
        return (
            <div className='flex flex-col bg-[#1D285A] p-4 rounded-[8px] mt-6' style={{ boxShadow: '0px 3px 7px rgba(28, 28, 34, 0.33)' }}>
                <span className='text-[18px] text-white font-bold'>
                    {isEmptyState ? `You don't have any positions currently, but you can add them now!` : 'You can add a position at any time'}
                </span>
                <PrimaryButton className='cursor-pointer mt-4 ' onClick={onAddPosition}>
                    <div className='flex items-center text-[20px]'>
                        <PlusIcon className='w-[16px] h-[16px] mr-2' />
                        <span>Add position</span>
                    </div>
                </PrimaryButton>
            </div>
        )
    }
    function renderAllRelevantPools() {
        // eslint-disable-next-line no-constant-condition
        if (isLoading) return renderLoadingScreen()
        if (!isLoading && positionsToUse.length === 0) return renderNoPositions()
        if (!positionsToUse || positionsToUse.length === 0) return null

        const positionsRendered = positionsToUse.map(renderPool)
        return (
            <div className='overflow-hidden'>
                {isLegacy ? null : renderNoPositions(false)}
                {positionsRendered}
            </div>
        )
    }

    function renderHeader() {
        if (isLegacy)
            return (
                <div className='mt-6 text-[22px] font-bold w-full border-b-[1px] border-[#0C3F6D] pb-3 lg:pb-6'>
                    <span>My Legacy Positions</span>
                </div>
            )
        return (
            <BoxHeaderNew slippage={DEFAULT_SLIPPAGE} className='' isRemoveSettings={true} name='My Current Positions'>
                <></>
                {/* <PrimaryButton onClick={onAddPosition} size='sm'>
                    <span className='px-4 flex text-[18px] normal-case '>Add Position</span>
                </PrimaryButton> */}
            </BoxHeaderNew>
        )
    }

    function renderVideoPopup() {
        const opts = {
            height: '100%',
            width: '100%',
            minHeight: '281px',
            playerVars: {
                // controls: 0,
                rel: 0
            }
            // playerVars: {
            //     // https://developers.google.com/youtube/player_parameters
            //     autoplay: 1
            // }
        }

        return (
            <Popup
                contentPadding='0'
                maxWidth={712}
                contentClassName='p-0 border-none'
                className='lg:w-[712px] lg:min-w-[712px] w-full padding-bottom-[25%] lg:padding-bottom-auto'
                open={isTutorialVideoVisible}
                onClose={() => setIsTutotrialVideoVisible(false)}>
                <>
                    <YouTube iframeClassName='min-h-[281px] lg:w-[712px] lg:h-[434px] w-full h-full' opts={opts} videoId='Vcm6aXVg3oc' />
                    <XIcon
                        onClick={() => {
                            setIsTutotrialVideoVisible(false)
                        }}
                        className='absolute right-[16px] lg:right-[-16px] top-[-16px] w-[12px] h-[12px] text-[white] z-[10] cursor-pointer'
                    />
                </>
            </Popup>
        )
    }
    function renderTutorialPrompt() {
        if (!isTutorialShow || isLegacy) return null
        return (
            <div
                onClick={() => setIsTutotrialVideoVisible(true)}
                className='fixed bottom-[32px] w-auto lg:w-full right-[16px] left-[16px] lg:left-auto  lg:bottom-0 lg:max-w-[320px] lg:right-[24px] cursor-pointer  lg:w-full h-[82px] rounded-[16px] mb-[24px] mt-[24px]'>
                {/* className='fixed bottom-[32px] right-[16px] left-[16px]  lg:bottom-0 lg:right-[100px] lg:max-w-[320px] cursor-pointer  w-full h-[82px] rounded-[16px] mb-[24px] mt-[24px]'> */}
                <XIcon
                    onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        window.localStorage.setItem('didViewVideo', 'true')
                        setIsTutorialShow(false)
                    }}
                    className='absolute right-[0px] lg:right-[0px] top-[-16px] w-[12px] h-[12px] text-[#68F2B0] z-[10] cursor-pointer'
                />
                <div className='absolute w-full h-full bg-[#2A7290] rounded-[16px] ' />
                <div className='flex w-full p-4 h-full items-center absolute z-[4] text-[#68F2B0] text-[16px]'>
                    <YoutubePlayIcon className='w-[80px] h-[45px] flex-shrink-0 mr-4 cursor-pointer' />

                    <div className='flex lg:text-[16px] text-[16px] flex-col'>
                        <span className=''>Explore our step-by-step guide for adding liquidity.</span>
                    </div>
                </div>
            </div>
        )
    }

    // if (isLegacy) console.log('postions ', postions)
    if ((!legacyPositons || legacyPositons.length === 0) && isLegacy) return null

    return (
        <div ref={myRef} className='flex-col max-w-[528px] w-full m-auto'>
            {renderHeader()}
            {renderTutorialPrompt()}
            {renderAllRelevantPools()}
            {renderMigrateLegacyPopup_phase0()}
            {renderRebalancePopup()}
            {renderVideoPopup()}
        </div>
    )
}
